import { InlineWidget } from "./PlayerAIInlineWidgets";
import { PlayerAIFailureNotice } from "./PlayerAIFailureNotice";
import type {
  PlayerAIChatMessage,
  PlayerAIWidgetActionHandler,
} from "./playerai.types";

/**
 * The `m.role === "system"` branch of MessageBubble (PB-5422
 * chat-parts-private) — a failure notice, a labelled product-authored notice,
 * or the plain centred pill. Split into its own file so MessageBubble.tsx
 * stays under the 200-line soft cap (packages/ui/CLAUDE.md invariant 6).
 * Moved as-is: no class, DOM or behaviour change.
 */
export function SystemNoticeBubble({
  m,
  onWidgetAction,
}: {
  m: PlayerAIChatMessage;
  onWidgetAction?: PlayerAIWidgetActionHandler | undefined;
}) {
  // Checked BEFORE the system-notice and pill branches: a failure is not a
  // product-authored status update, and the centred pill has nowhere to put
  // a recovery action or the copy control.
  if (m.failureNotice) {
    return (
      <PlayerAIFailureNotice
        notice={m.failureNotice}
        {...(onWidgetAction !== undefined ? { onAction: onWidgetAction } : {})}
      />
    );
  }
  if (m.systemNotice) {
    const timestamp = formatSystemNoticeTime(m.ts);
    return (
      <section
        aria-label={m.systemNotice.label}
        className="w-full rounded-lg border border-pb-border/70 bg-pb-card/60 px-3 py-3 text-start"
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-dense-2 font-normal uppercase tracking-wider text-pb-text-muted">
            {m.systemNotice.label}
          </span>
          {timestamp && m.ts ? (
            <time
              dateTime={m.ts}
              className="shrink-0 text-dense-2 font-normal text-pb-text-muted"
            >
              {timestamp}
            </time>
          ) : null}
        </div>
        <div className="mt-2 space-y-1 text-sm leading-snug">
          {m.content ? (
            <p className="font-normal text-pb-text">{m.content}</p>
          ) : null}
          {m.systemNotice.details.map((detail, index) => (
            <p
              key={`${m.id}-system-detail-${index}`}
              className="font-normal text-pb-text-secondary"
            >
              {detail}
            </p>
          ))}
        </div>
        {m.widget ? (
          <div className="mt-2">
            <InlineWidget
              payload={m.widget}
              {...(onWidgetAction !== undefined
                ? { onAction: onWidgetAction }
                : {})}
            />
          </div>
        ) : null}
      </section>
    );
  }
  return (
    <div className="text-center">
      <span className="text-dense-2 uppercase tracking-wider text-pb-text-muted bg-pb-card/60 border border-pb-border/50 rounded-full px-2 py-0.5">
        {m.content}
      </span>
      {m.widget ? (
        // A server-authored system notice may carry one bounded affordance
        // (e.g. the workshop's "Follow facilitator" action). Rendered under
        // the pill so the notice stays legible without one.
        <div className="mt-2 flex justify-center">
          <InlineWidget
            payload={m.widget}
            {...(onWidgetAction !== undefined
              ? { onAction: onWidgetAction }
              : {})}
          />
        </div>
      ) : null}
    </div>
  );
}

function formatSystemNoticeTime(ts: string | undefined): string | null {
  if (!ts) return null;
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
